import { DEFAULT_MAX_IMAGE_SIZE, IMAGE_QUALITY } from '@/lib/constants';
import { isImageMimeTypeSupported } from '@/lib/supported-formats';
import type { ExtractTextResponse } from '@/app/[locale]/api/extract-text/route';

export interface ProcessImageOptions {
  file: File;
  sourceLang?: string;
  maxImageSize?: number;
  onTextExtracted: (text: string, context?: string) => void;
  onProcessingChange?: (isProcessing: boolean) => void;
}

export const processImageFile = async ({
  file,
  sourceLang,
  maxImageSize = DEFAULT_MAX_IMAGE_SIZE,
  onTextExtracted,
  onProcessingChange
}: ProcessImageOptions) => {
  onProcessingChange?.(true);

  try {
    // Validate file type against supported formats
    if (!isImageMimeTypeSupported(file.type)) {
      throw new Error(`Unsupported image format: ${file.type}. Supported formats: PNG, JPEG, WEBP, HEIC, HEIF`);
    }

    // Resize image if necessary
    const resizedFile = await resizeImage(file, maxImageSize);

    // Convert file to base64
    const base64 = await convertToBase64(resizedFile);
    const base64Data = base64.split(',')[1]; // Remove the data:image/...;base64, prefix

    // Call the extract text API
    const response = await fetch('/api/extract-text', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        image: {
          data: base64Data,
          fileName: resizedFile.name,
          mimeType: resizedFile.type
        },
        sourceLang: sourceLang
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to extract text from image');
    }

    const result: ExtractTextResponse = await response.json();

    if (result.extractedText.trim()) {
      onTextExtracted(result.extractedText, result.context);
    } else {
      console.warn('No text found in the uploaded image');
    }
  } catch (error) {
    console.error('Image processing error:', error);
  } finally {
    onProcessingChange?.(false);
  }
};

const convertToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const resizeImage = (file: File, maxSize: number): Promise<File> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = document.createElement('img');

    img.onload = () => {
      // Calculate new dimensions
      let { width, height } = img;

      if (width <= maxSize && height <= maxSize) {
        // Image is already within limits, return original
        resolve(file);
        return;
      }

      // Calculate scaling factor to fit within maxSize x maxSize
      const scaleFactor = Math.min(maxSize / width, maxSize / height);
      const newWidth = Math.floor(width * scaleFactor);
      const newHeight = Math.floor(height * scaleFactor);

      // Set canvas dimensions
      canvas.width = newWidth;
      canvas.height = newHeight;

      // Draw resized image
      ctx?.drawImage(img, 0, 0, newWidth, newHeight);

      // Convert canvas to blob
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const resizedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now(),
            });
            resolve(resizedFile);
          } else {
            reject(new Error('Failed to resize image'));
          }
        },
        file.type,
        IMAGE_QUALITY
      );
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
};
